// Theme_Modal.jsx
import { useState, useEffect } from "react";
import PropTypes from "prop-types";
import { Modal, Radio } from "@/config/antd";
import { CloseOutlined } from "@/config/antd";
import WebIcon from "@/components/common/Web_Icons";
import Button from "@/components/common/Button";
import { useLoader } from "@/hooks/LoaderProvider";
import Success from "@/components/common/alerts/Success";
import { useTheme } from "@/hooks/ThemeContext";

const ThemeModal = ({ isVisible, onClose }) => {
  const { theme, toggleTheme } = useTheme();
  const [selectedTheme, setSelectedTheme] = useState(theme);
  const [tempTheme, setTempTheme] = useState(theme);
  const [isSuccessVisible, setIsSuccessVisible] = useState(false);
  const { showLoader, hideLoader } = useLoader();

  useEffect(() => {
    if (isVisible) {
      setSelectedTheme(theme);
      setTempTheme(theme);
    }
  }, [isVisible, theme]);

  const themeOptions = [
    {
      id: "light",
      title: "Light Mode",
      icon: "BlueDayMode",
    },
    {
      id: "dark",
      title: "Dark Mode",
      icon: "BlueNightMode",
    },
    {
      id: "default",
      title: "System Default",
      icon: "BlueSystemDefault",
    },
  ];

  const handleThemeChange = (e) => {
    const newTheme = e.target.value;
    setSelectedTheme(newTheme);
    // Temporarily apply theme
    toggleTheme(newTheme);
  };

  const handleCancel = () => {
    // Revert to original theme
    toggleTheme(tempTheme);
    onClose();
  };

  const handleUpdate = () => {
    // Persist the theme
    setTempTheme(selectedTheme);
    onClose();
    showLoader();

    setTimeout(() => {
      hideLoader();
      setIsSuccessVisible(true);
    }, 2000);
  };

  const handleSuccessClose = () => {
    setIsSuccessVisible(false);
  };

  return (
    <>
      <Modal
        open={isVisible}
        onCancel={handleCancel}
        footer={null}
        centered
        width={500}
        closeIcon={null}
        styles={{
          body: { padding: 0 },
          content: {
            padding: 0,
            borderRadius: "16px",
            overflow: "hidden",
          },
          mask: { backgroundColor: "rgba(0, 0, 0, 0.75)" },
        }}>
        <div className="flex flex-col bg-white">
          {/* Header */}
          <div className="p-4 flex items-center justify-between border-b border-gray-200">
            <h2 className="text-bold-x text-primary">App Theme</h2>
            <CloseOutlined
              onClick={handleCancel}
              className="cursor-pointer"
              style={{
                color: "var(--veegil-color-primary)",
                backgroundColor: "var(--veegil-color-secondary)",
                borderRadius: "50%",
                padding: "4px",
              }}
            />
          </div>

          {/* Body */}
          <div className="p-6">
            <div className="space-y-4">
              <Radio.Group
                value={selectedTheme}
                onChange={handleThemeChange}
                className="w-full"
                style={{
                  "--ant-primary-color": "var(--veegil-color-primary)",
                }}>
                {themeOptions.map((option) => (
                  <div
                    key={option.id}
                    className="flex items-center justify-between p-4 bg-white rounded-lg cursor-pointer transition-all duration-300">
                    <div className="flex items-center gap-4">
                      <div className="w-10 h-10 flex items-center justify-center">
                        <WebIcon
                          icon={option.icon}
                          className="w-[50px] h-[50px]"
                        />
                      </div>
                      <div>
                        <h3 className="text-bold text-black">{option.title}</h3>
                      </div>
                    </div>
                    <Radio value={option.id} className="custom-radio" />
                  </div>
                ))}
              </Radio.Group>
            </div>
          </div>

          {/* Footer */}
          <div className="w-full bg-secondary p-4 flex gap-3">
            <Button
              text="Cancel"
              onClick={handleCancel}
              customBgColor="white"
              customTextColor="primary"
              className="flex-1 border border-primary"
            />
            <Button
              text="Update"
              onClick={handleUpdate}
              isActive={true}
              color="primary"
              className="flex-1"
              customTextColor="white"
            />
          </div>
        </div>
      </Modal>

      <Success
        isVisible={isSuccessVisible}
        onClose={handleSuccessClose}
        header="Theme Updated"
        paragraph="Your new default theme has been saved successfully."
        cancelButtonText="Cancel"
        buttonText="CLOSE"
      />
    </>
  );
};

ThemeModal.propTypes = {
  isVisible: PropTypes.bool,
  onClose: PropTypes.func,
};

export default ThemeModal;
